import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useState, ReactElement } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import Modal from './Modal'
import NumberInput from './NumberInput'
import { ManagedPaymentMethod, getMethod } from '../util/Managed'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import PaymentMethodDisplay from './PaymentMethodDisplay'
import { LinkArrow } from '../icons/LinkArrow'
import Radom, { UserRFQRequest, IPaymentMethod } from '../state/Radom'
import _ from 'lodash'
import { Chevron } from '../icons/Chevron'
import LoadingBar from './LoadingBar'
import ErrorIcon from '../icons/ErrorIcon'
import { DropdownItem } from './Dropdown'
import Checkbox from './Checkbox'

export interface ConvertibleAsset extends IPaymentMethod {
  maxAmount?: string
}

interface ConversionRowProps {
  asset: ConvertibleAsset
  label: string
  amount: string
  onAmount: (v: string) => void
  autoFocus?: boolean
  onClick?: () => void
  loading?: boolean
}

interface IProps {
  modalOpen: boolean
  onClose: () => void
  fromAsset: IPaymentMethod
  selectedIntoAsset: IPaymentMethod
  availableIntoAssets: IPaymentMethod[]
  onConvert: (quoteId: string) => Promise<void>
}

const ConversionRowDiv = styled.div`
  padding: 20px;
  width: 450px;
  max-width: 100%;
  background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  border-radius: 20px;
`

export const ConversionRow = ({
  asset, amount, label, onAmount, autoFocus, onClick, loading
}: ConversionRowProps): ReactElement => {
  const isConvertTarget = loading !== undefined
  const maxAmount: string | undefined = asset.maxAmount
  return (<ConversionRowDiv>
    <span style={{
      fontSize: 12,
      textTransform: 'uppercase',
      fontFamily: 'var(--font-mono)'
    }}>
      {label}
    </span>
    <div style={{ display: 'flex', gap: 10 }}>

      {loading
        ? <div style={{ width: '100%' }}>
          <LoadingBar style={{ height: 30, marginBlock: 10, width: '45%' }} />
        </div>
        : <NumberInput
          autoFocus={autoFocus}
          onUserInput={onAmount}
          placeholder='0'
          value={amount}
          style={{
            fontSize: 22,
            border: 0,
            padding: '10px 0',
            boxShadow: 'none',
            background: 'transparent'
          }}
          disabled={isConvertTarget} />}

      <PrimaryButton
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          backgroundColor: RADOM_COLORS.GRAY_MED,
          padding: 10,
          borderRadius: 20,
          marginBottom: 12,
          cursor: 'pointer',
          userSelect: 'none',
          color: 'black',
          boxShadow: 'none'
        }}
        onClick={onClick}>
        <PaymentMethodDisplay
          size={28}
          method={asset}
          excludeName />
        <span style={{ fontSize: 14, fontWeight: 500, marginLeft: asset.token ? 4 : 0 }}>
          {getMethodSafe(asset)?.ticker}
        </span>
        {isConvertTarget &&
          <Chevron
            style={{
              opacity: 0.3,
              transition: '0.2s ease all',
              minWidth: 15,
              transform: 'rotate(0deg)' // Rotate the arrow to point right
            }} />
        }
      </PrimaryButton>
    </div>
    {maxAmount &&
      <div>
        <div style={{ display: 'flex', fontSize: 12, gap: 3, alignItems: 'center', justifyContent: 'flex-end' }}>
          <span style={{ opacity: 0.5 }}>Balance</span>
          <span>
            {
              new Intl.NumberFormat('en-US', {
                maximumSignificantDigits: 18
              }).format(Number(maxAmount))
            }
          </span>
          <SecondaryButton
            onClick={() => onAmount(maxAmount)}
            style={{
              marginLeft: 5,
              padding: '4px 8px',
              borderRadius: 5,
              border: 0,
              fontSize: 10,
              textTransform: 'uppercase',
              backgroundColor: RADOM_COLORS.INFO_LIGHT,
              color: RADOM_COLORS.BLUE4,
              fontWeight: 600
            }}>Max</SecondaryButton>
        </div>
      </div>}
  </ConversionRowDiv>)
}

export function getMethodSafe(ipm: IPaymentMethod): ManagedPaymentMethod | undefined {
  const { network, token } = ipm
  const result = getMethod(network, token)

  // This is how they represent the empty case currently:
  if (result?.name === 'Unknown' && result?.ticker === 'Unknown' && result?.logo === null) {
    return undefined
  }

  return result
}

interface Quote {
  quoteId: string
  quotedAmount: string
}

type QuoteState = Quote | undefined | 'loading' | 'failed' | 'accepting'

interface QuoteFetcher {
  quote: QuoteState
  requote: () => void
  setAccept: () => void
  clearQuote: () => void
}

const useQuoteFetcher = (req: UserRFQRequest): QuoteFetcher => {
  const [quote, setQuote] = useState<QuoteState>(undefined)

  const amount = req.fromAmount

  const getQuote = async (): Promise<void> => {
    if (!amount) return
    setQuote('loading')

    try {
      const { quotedAmount, quoteId } = await Radom.rfq(req)
      console.log(`Fetching quote for amount: ${quotedAmount} (ID: ${quoteId})`)
      setQuote({ quoteId, quotedAmount })
    } catch (err) {
      console.error('Failed to initiate RFQ', err)
      setQuote('failed')
    }
  }

  const fetchData = useCallback(getQuote, [amount, req.into])
  const debouncedFetch = useCallback(_.debounce(fetchData, 1000), [amount, req.into])

  useEffect(() => {
    if (!amount) {
      setQuote(undefined)
      return
    }

    debouncedFetch()

    return () => {
      debouncedFetch.cancel()
    }
  }, [amount, req.into])

  const clearQuote = (): void => {
    setQuote('loading')
  }

  const setAccept = (): void => setQuote('accepting')

  return { quote, requote: getQuote, setAccept, clearQuote }
}

const AssetConversionModal = observer((props: IProps): ReactElement => {
  const [fromAmount, setFromAmount] = useState('')

  const [selectionModalOpen, setSelectionModalOpen] = useState(false)
  const [selectedIntoAsset, setSelectedIntoAsset] = useState<IPaymentMethod>(props.selectedIntoAsset)

  const { quote, requote, setAccept, clearQuote } = useQuoteFetcher({
    fromAmount,
    from: props.fromAsset,
    into: selectedIntoAsset
  })

  return (
    <>
      <Modal visible={props.modalOpen}
        innerStyles={{ overflow: 'hidden' }}
        onClose={() => props.onClose()}
        title={selectionModalOpen ? 'Select asset' : 'Convert'}>
        <div style={{ width: 500, minHeight: selectionModalOpen ? 400 : 335, transition: '0.2s ease all' }}>

          {
            selectionModalOpen &&
            <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 400, overflowY: 'auto', width: '100%' }}>
              {props.availableIntoAssets.map((asset, index) => {
                const safeAsset = getMethodSafe(asset)
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      if (asset !== selectedIntoAsset) {
                        setSelectedIntoAsset(asset)
                        clearQuote() // TODO: this should be when
                      }
                      setSelectionModalOpen(false)
                    }}
                    style={{
                      backgroundColor: asset === selectedIntoAsset ? RADOM_COLORS.GRAY_MED : '',
                      padding: '5px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 15,
                      gap: 10
                    }}>
                      <Checkbox isRadio checked={asset === selectedIntoAsset} />
                      <PaymentMethodDisplay
                        method={asset}
                        excludeName />

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: 14
                      }}>
                        <span>{safeAsset?.name}</span>
                        <span style={{ opacity: 0.5 }}>{safeAsset?.ticker}{safeAsset?.hostChain && ` on ${safeAsset?.hostChain.name}`}</span>
                      </div>
                    </div>

                    <div style={{ padding: 20 }}>
                      <Chevron style={{ transform: 'rotate(-90deg)', opacity: asset === selectedIntoAsset ? 1 : 0.5 }} />
                    </div>
                  </DropdownItem>
                )
              })}
            </div>
          }

          {
            !selectionModalOpen &&
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '0 30px 30px 30px',
              gap: 12,
              alignItems: 'center'
            }}>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                alignItems: 'center'
              }}>

                <ConversionRow
                  autoFocus
                  label='From'
                  asset={props.fromAsset}
                  amount={fromAmount}
                  onAmount={amount => setFromAmount(amount)} />

                <div style={{
                  margin: '-26px',
                  width: 40,
                  height: 40,
                  background: 'white',
                  zIndex: 1,
                  borderRadius: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LinkArrow style={{ transform: 'rotate(90deg)', width: 24 }} stroke={RADOM_COLORS.GRAY_DARKER} />
                </div>

                <ConversionRow
                  label='Into'
                  asset={selectedIntoAsset}
                  amount={typeof quote === 'object' ? quote.quotedAmount : ''}
                  onAmount={amount => setFromAmount(amount)}
                  onClick={() => setSelectionModalOpen(true)}
                  loading={quote === 'loading'}
                />
              </div>

              {
                quote === 'failed'
                  ? <>
                    <div style={{ color: 'red', fontSize: 14, display: 'flex', gap: 5, alignItems: 'center', width: '100%' }}>
                      <ErrorIcon style={{ height: 20, width: 20 }} />
                      <span>Error fetching conversion quote. Please try again.</span>
                    </div>
                    <PrimaryButton
                      onClick={requote}
                      style={{ width: '100%', backgroundColor: 'red' }}>
                    Retry
                    </PrimaryButton>
                  </>
                  : <PrimaryButton
                    onClick={() => {
                      if (quote && quote !== 'loading' && typeof quote === 'object') {
                        setAccept()
                        props.onConvert(quote.quoteId).catch(err => {
                          console.error('Error accepting quote', err)
                        })
                      } else {
                        console.error('No quote to convert')
                      }
                    }}
                    disabled={!quote || quote === 'accepting'}
                    isLoading={(fromAmount !== '' && quote === 'loading') || quote === 'accepting'}
                    style={{ width: '100%' }}>
                    Convert
                  </PrimaryButton>
              }
            </div>
          }

        </div>
      </Modal>
    </>
  )
})

export default AssetConversionModal
